/**
 * Advertising — one consolidated view across every connected ad platform, with
 * the same numbers filterable to a single channel.
 *
 * WRITE-ENABLED for Amazon (Isaac, 2026-09-14): the Campaigns tab can pause/
 * enable a campaign and edit its budget directly, via
 * POST /advertising/campaigns/:id/{state,budget} — a click here already IS
 * the approval (see server/routes/advertising.ts's file header). eBay and
 * Walmart have no write support on the backend yet, so their rows show the
 * same columns but no action controls rather than a control that would 501.
 *
 * Superseded note, kept for history: "Read-only by design: nothing on this
 * page can change a bid, a budget or a campaign's state. The only action is
 * pulling fresh data."
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowUpDown,
  CheckCircle2,
  Lightbulb,
  Megaphone,
  Pause,
  Pencil,
  Play,
  Plus,
  RefreshCw,
  TriangleAlert,
  XCircle,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { NewCampaignDialog } from "@/components/NewCampaignDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ApiClientError, api, apiWithMeta } from "../lib/api";
import { integer, money, whenever } from "../lib/format";
import i18n from "../lib/i18n";
import { cn } from "../lib/utils";

type Period = "7d" | "30d" | "60d" | "90d" | "180d";
type Tab = "overview" | "campaigns" | "skus" | "orders";
type OrderStatus =
  | "pending"
  | "unshipped"
  | "partially_shipped"
  | "shipped"
  | "delivered"
  | "cancelled"
  | "refunded";

/** "Active" = still needs attention -- not yet shipped, not cancelled/
 * refunded. Matches Sales' own OrderStatus enum (server/../shared/
 * operations.ts's orderStatusEnum), just scoped down for a quick-glance
 * widget rather than the full grid Sales already provides. */
const ACTIVE_ORDER_STATUSES: OrderStatus[] = ["pending", "unshipped", "partially_shipped"];

/** Same mapping as Sales.tsx's STATUS_VARIANT -- kept local rather than
 * imported since each page here owns its own small display constants, but
 * intentionally identical so a status badge never looks different depending
 * on which page you saw it on. */
const ORDER_STATUS_VARIANT: Record<
  OrderStatus,
  "secondary" | "info" | "success" | "destructive" | "outline"
> = {
  pending: "secondary",
  unshipped: "info",
  partially_shipped: "info",
  shipped: "success",
  delivered: "success",
  cancelled: "destructive",
  refunded: "outline",
};
type CampaignSortKey =
  | "name"
  | "status"
  | "budget"
  | "impressions"
  | "spend"
  | "sales"
  | "acos"
  | "roas"
  | "clicks"
  | "cpc";
type SortDir = "asc" | "desc";

/** Amazon is the only marketplace whose AdsClient implements the write
 * methods today (see server/lib/ads/types.ts) — eBay/Walmart rows simply get
 * no action controls rather than a button that would 501. */
function canControlCampaign(marketplace: string): boolean {
  return marketplace === "amazon";
}

interface AdStatus {
  channelId: string;
  marketplace: string;
  label: string;
  configured: boolean;
}

interface AdTotals {
  spend: string;
  attributedSales: string;
  impressions: number;
  clicks: number;
  attributedOrders: number;
  attributedUnits: number;
  ctr: number;
  cpc: number;
  acos: number | null;
  roas: number | null;
}

interface AdSummary {
  total: AdTotals;
  byChannel: Array<AdTotals & { channelId: string; marketplace: string }>;
  period: Period;
  from: string;
  to: string;
}

interface AdCampaignRow extends AdTotals {
  id: string;
  name: string;
  status: string;
  platformStatus: string | null;
  campaignType: string | null;
  dailyBudget: string | null;
  currency: string;
  marketplace: string;
}

interface AdSkuRow extends AdTotals {
  channelSku: string;
  productId: string | null;
  productSku: string | null;
  productTitle: string | null;
  marketplace: string;
  realRevenue: string | null;
  realUnits: number | null;
  trueAcos: number | null;
}

/** One row from the autopilot's dry-run advisory pass — see
 * GET /advertising/recommendations. Purely advisory: `reason` already
 * carries the full human-readable explanation from server/lib/ads/decisions.ts,
 * so the UI doesn't need to reconstruct it from the other fields. */
interface AdRecommendationRow {
  id: string;
  campaignId: string;
  campaignName: string;
  marketplace: string;
  entityKind: "campaign" | "keyword";
  actionType: "pause" | "enable" | "set_budget" | "set_bid";
  newValue: string | null;
  reason: string;
  metricAcos: string | null;
  targetAcos: string | null;
}

/** Subset of Sales.tsx's OrderRow -- only what this widget actually
 * displays, from the same GET /api/v1/orders the Sales page uses. */
interface AdOrderRow {
  id: string;
  marketplace: string;
  accountLabel: string;
  orderNumber: string;
  status: OrderStatus;
  currency: string;
  total: string;
  buyerName: string | null;
  orderedAt: string;
  itemCount: number;
}

const PERIODS: Period[] = ["7d", "30d", "60d", "90d", "180d"];

export default function Advertising() {
  const { t } = useTranslation(["advertising", "navigation", "common", "sales"]);
  const queryClient = useQueryClient();
  const locale = i18n.language;

  const [period, setPeriod] = useState<Period>("30d");
  const [channelId, setChannelId] = useState<string>("all");
  const [tab, setTab] = useState<Tab>("overview");
  const [matched, setMatched] = useState<"all" | "matched" | "unmatched">("all");
  const [sort, setSort] = useState<"spend" | "sales" | "acos" | "clicks" | "impressions">("spend");
  const [campaignSort, setCampaignSort] = useState<{ key: CampaignSortKey; dir: SortDir }>({
    key: "spend",
    dir: "desc",
  });
  const [campaignFilter, setCampaignFilter] = useState<"enabled" | "all">("all");
  const [newCampaignOpen, setNewCampaignOpen] = useState(false);
  const [campaignActionError, setCampaignActionError] = useState<string | null>(null);
  const [budgetDialog, setBudgetDialog] = useState<{
    campaignId: string;
    name: string;
    currency: string;
  } | null>(null);
  const [budgetDraft, setBudgetDraft] = useState("");
  const [syncAllToast, setSyncAllToast] = useState<"success" | "error" | null>(null);
  // Same reasoning as Sales.tsx's identical state: an ad channel pull can
  // take minutes (Amazon's report submit/poll/download cycle in particular),
  // so each channel needs to signal its OWN completion rather than the whole
  // row staying frozen until the slowest channel finishes.
  const [batchSyncingIds, setBatchSyncingIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!syncAllToast) return;
    const timer = setTimeout(() => setSyncAllToast(null), 5000);
    return () => clearTimeout(timer);
  }, [syncAllToast]);

  const scope = channelId === "all" ? "" : `&channelId=${channelId}`;

  const statusQuery = useQuery({
    queryKey: ["advertising", "status"],
    // apiWithMeta, not api: api() unwraps the `data` envelope itself, and this
    // is the one query here that also needs `meta.lastSyncedAt`.
    queryFn: () =>
      apiWithMeta<AdStatus[], { lastSyncedAt: string | null }>("/api/v1/advertising/status"),
  });

  const summaryQuery = useQuery({
    queryKey: ["advertising", "summary", period, channelId],
    queryFn: () => api<AdSummary>(`/api/v1/advertising/summary?period=${period}${scope}`),
  });

  const trendQuery = useQuery({
    queryKey: ["advertising", "trend", period, channelId],
    queryFn: () =>
      api<Array<{ date: string; spend: string; sales: string }>>(
        `/api/v1/advertising/trend?period=${period}${scope}`,
      ),
  });

  // Not scoped by period/channelId -- this is always "the last autopilot
  // run," a separate concept from the spend-window filters above.
  const recommendationsQuery = useQuery({
    queryKey: ["advertising", "recommendations"],
    queryFn: () =>
      apiWithMeta<AdRecommendationRow[], { runAt: string | null }>(
        "/api/v1/advertising/recommendations",
      ),
  });

  const campaignsQuery = useQuery({
    queryKey: ["advertising", "campaigns", period, channelId],
    queryFn: () => api<AdCampaignRow[]>(`/api/v1/advertising/campaigns?period=${period}${scope}`),
    enabled: tab === "campaigns",
  });

  const skusQuery = useQuery({
    queryKey: ["advertising", "skus", period, channelId, matched, sort],
    queryFn: () =>
      api<AdSkuRow[]>(
        `/api/v1/advertising/skus?period=${period}${scope}&matched=${matched}&sort=${sort}`,
      ),
    enabled: tab === "skus",
  });

  // Not scoped by period/channelId, same reasoning as recommendationsQuery --
  // "orders open right now" is its own concept. GET /api/v1/orders only
  // takes one status at a time (Sales.tsx's own filter is single-select
  // too), so this fires one request per active status and merges -- each
  // individually small (163 pending account-wide as of 2026-09-15), not
  // worth a backend change for what's meant to be a quick-glance widget, not
  // a replacement for the full Sales grid.
  //
  // fulfillmentChannel=merchant, always -- Isaac (2026-09-15): "I have no
  // control or [say in] when FBA orders ship to customers, I want to see
  // what I have to ship." This widget is specifically the merchant-fulfilled
  // queue, not a general open-orders view; Amazon FBA orders never belong
  // here regardless of status.
  const activeOrdersQuery = useQuery({
    queryKey: ["advertising", "active-orders"],
    queryFn: async () => {
      const perStatus = await Promise.all(
        ACTIVE_ORDER_STATUSES.map((status) =>
          api<AdOrderRow[]>(
            `/api/v1/orders?status=${status}&fulfillmentChannel=merchant&limit=100`,
          ),
        ),
      );
      return perStatus
        .flat()
        .sort((a, b) => new Date(b.orderedAt).getTime() - new Date(a.orderedAt).getTime());
    },
    enabled: tab === "orders",
  });

  const syncMutation = useMutation({
    mutationFn: (id: string) => api(`/api/v1/advertising/channels/${id}/sync`, { method: "POST" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["advertising"] }),
  });

  const syncAllMutation = useMutation({
    mutationFn: async () => {
      const configured = (statusQuery.data?.data ?? []).filter((s) => s.configured);
      setBatchSyncingIds(new Set(configured.map((s) => s.channelId)));
      return Promise.allSettled(
        configured.map((s) =>
          api(`/api/v1/advertising/channels/${s.channelId}/sync`, { method: "POST" }).finally(
            () => {
              setBatchSyncingIds((prev) => {
                const next = new Set(prev);
                next.delete(s.channelId);
                return next;
              });
            },
          ),
        ),
      );
    },
    onSuccess: (results) => {
      queryClient.invalidateQueries({ queryKey: ["advertising"] });
      setSyncAllToast(results.every((r) => r.status === "fulfilled") ? "success" : "error");
    },
    onError: () => setSyncAllToast("error"),
  });

  // Manual controls — a click here already IS the approval (see the file
  // header and server/lib/ads/decisions.ts), so these apply immediately
  // rather than going through any queue.
  const campaignStateMutation = useMutation({
    mutationFn: ({ id, state }: { id: string; state: "enabled" | "paused" }) =>
      api(`/api/v1/advertising/campaigns/${id}/state`, {
        method: "POST",
        body: JSON.stringify({ state }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["advertising"] }),
    onError: (err) =>
      setCampaignActionError(
        err instanceof ApiClientError ? err.message : t("advertising:campaigns.actionFailed"),
      ),
  });

  const campaignBudgetMutation = useMutation({
    mutationFn: ({ id, dailyBudget }: { id: string; dailyBudget: string }) =>
      api(`/api/v1/advertising/campaigns/${id}/budget`, {
        method: "POST",
        body: JSON.stringify({ dailyBudget }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["advertising"] });
      setBudgetDialog(null);
    },
    onError: (err) =>
      setCampaignActionError(
        err instanceof ApiClientError ? err.message : t("advertising:campaigns.actionFailed"),
      ),
  });

  const statuses = statusQuery.data?.data ?? [];
  const connected = statuses.filter((s) => s.configured);
  const totals = summaryQuery.data?.total;

  /** Client-side sort — the full campaign list for the period is already in
   * hand from campaignsQuery, so sorting server-side would just be a second
   * round trip for no new data. */
  /** Defaults to "all" -- Isaac (2026-09-15), revising the same-day earlier
   * default of hiding archived/zero-spend campaigns automatically: keep
   * everything visible by default, but offer a plain status filter so
   * switching to just what's enabled is one click, not the default. Status
   * only, deliberately not also requiring spend > 0 -- "enabled" should mean
   * exactly what Amazon calls enabled, not a second, stricter definition. */
  const visibleCampaigns = useMemo(() => {
    const all = campaignsQuery.data ?? [];
    if (campaignFilter === "all") return all;
    return all.filter((c) => c.status === "enabled");
  }, [campaignsQuery.data, campaignFilter]);

  const sortedCampaigns = useMemo(() => {
    const rows = [...visibleCampaigns];
    const value = (c: AdCampaignRow): number | string => {
      switch (campaignSort.key) {
        case "name":
          return c.name.toLowerCase();
        case "status":
          return c.status;
        case "budget":
          return c.dailyBudget != null ? Number(c.dailyBudget) : -1;
        case "impressions":
          return c.impressions;
        case "spend":
          return Number(c.spend);
        case "sales":
          return Number(c.attributedSales);
        // Nulls last in both directions: "no sales" is not the best ACOS/ROAS,
        // it is the absence of one.
        case "acos":
          return c.acos ?? Number.POSITIVE_INFINITY;
        case "roas":
          return c.roas ?? -1;
        case "clicks":
          return c.clicks;
        case "cpc":
          return c.cpc;
        default:
          return 0;
      }
    };
    rows.sort((a, b) => {
      const av = value(a);
      const bv = value(b);
      const cmp = typeof av === "string" ? av.localeCompare(bv as string) : av - (bv as number);
      return campaignSort.dir === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [visibleCampaigns, campaignSort]);

  function toggleCampaignSort(key: CampaignSortKey) {
    setCampaignSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: key === "name" || key === "status" ? "asc" : "desc" },
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 text-3xl font-semibold tracking-tight">
            <Megaphone className="h-7 w-7 shrink-0" aria-hidden />
            {t("advertising:title")}
          </h1>
          <p className="mt-1 text-muted-foreground">{t("advertising:subtitle")}</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Select value={channelId} onValueChange={setChannelId}>
            <SelectTrigger className="w-[11rem]" aria-label={t("advertising:allChannels")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("advertising:allChannels")}</SelectItem>
              {/* Only channels actually connected -- an unconfigured one
                  (e.g. Walmart today) would just filter to zero data, which
                  reads as broken rather than "not connected yet". */}
              {statuses
                .filter((s) => s.configured)
                .map((s) => (
                  <SelectItem key={s.channelId} value={s.channelId}>
                    {s.marketplace.charAt(0).toUpperCase() + s.marketplace.slice(1)}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>

          <Select value={period} onValueChange={(v) => setPeriod(v as Period)}>
            <SelectTrigger className="w-[7.5rem]" aria-label={t("advertising:period")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIODS.map((p) => (
                <SelectItem key={p} value={p}>
                  {p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </header>

      {/* Per-platform connection state. Shown even when nothing is connected,
          because "connected but spent nothing" and "not connected at all" are
          different answers and the numbers alone cannot tell them apart. */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2 pb-3">
          <CardTitle className="text-base">{t("advertising:tabs.overview")}</CardTitle>
          <div className="flex items-center gap-2">
            {syncAllMutation.isPending && (
              <span role="status" className="text-sm text-muted-foreground">
                {t("advertising:syncAllProgress", {
                  done: connected.length - batchSyncingIds.size,
                  total: connected.length,
                })}
              </span>
            )}
            {!syncAllMutation.isPending && syncAllToast && (
              <span
                role="status"
                className={cn(
                  "flex items-center gap-1 text-sm",
                  syncAllToast === "success" ? "text-emerald-500" : "text-destructive",
                )}
              >
                {syncAllToast === "success" ? (
                  <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />
                ) : (
                  <XCircle className="h-4 w-4 shrink-0" aria-hidden />
                )}
                {syncAllToast === "success"
                  ? t("advertising:syncAllSuccess")
                  : t("advertising:syncAllError")}
              </span>
            )}
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={syncAllMutation.isPending || connected.length === 0}
              onClick={() => syncAllMutation.mutate()}
            >
              <RefreshCw
                className={cn("h-3.5 w-3.5", syncAllMutation.isPending && "animate-spin")}
                aria-hidden
              />
              {t("advertising:syncAll")}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-3">
            {statuses.map((s) => (
              <div
                key={s.channelId}
                className="flex min-w-0 items-center gap-3 rounded-lg border px-3 py-2"
              >
                <span className="font-medium capitalize">{s.marketplace}</span>
                {s.configured ? (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={syncMutation.isPending || batchSyncingIds.has(s.channelId)}
                    onClick={() => syncMutation.mutate(s.channelId)}
                  >
                    <RefreshCw
                      className={cn(
                        "mr-2 h-3.5 w-3.5",
                        (syncMutation.isPending || batchSyncingIds.has(s.channelId)) &&
                          "animate-spin",
                      )}
                      aria-hidden
                    />
                    {syncMutation.isPending || batchSyncingIds.has(s.channelId)
                      ? t("advertising:syncing")
                      : t("advertising:sync")}
                  </Button>
                ) : (
                  <Badge variant="outline" title={t("advertising:notConnectedHint")}>
                    {t("advertising:notConnected")}
                  </Badge>
                )}
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            {t("advertising:lastSynced")}:{" "}
            {statusQuery.data?.meta?.lastSyncedAt
              ? whenever(statusQuery.data.meta.lastSyncedAt, locale)
              : t("advertising:never")}
            {" · "}
            {t("advertising:controlNote")}
          </p>
        </CardContent>
      </Card>

      {connected.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-start gap-3 py-10">
            <TriangleAlert className="h-8 w-8 text-muted-foreground" aria-hidden />
            <h2 className="text-lg font-semibold">{t("advertising:nothingConnected")}</h2>
            <p className="max-w-3xl text-muted-foreground">
              {t("advertising:nothingConnectedBody")}
            </p>
          </CardContent>
        </Card>
      ) : null}

      {/* KPI row */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi
          label={t("advertising:kpi.spend")}
          value={money(totals?.spend ?? "0", "USD", locale)}
        />
        <Kpi
          label={t("advertising:kpi.sales")}
          value={money(totals?.attributedSales ?? "0", "USD", locale)}
        />
        <Kpi
          label={t("advertising:kpi.acos")}
          value={totals?.acos != null ? `${totals.acos}%` : "—"}
        />
        <Kpi
          label={t("advertising:kpi.roas")}
          value={totals?.roas != null ? `${totals.roas}×` : "—"}
        />
        <Kpi
          label={t("advertising:kpi.impressions")}
          value={integer(totals?.impressions ?? 0, locale)}
        />
        <Kpi label={t("advertising:kpi.clicks")} value={integer(totals?.clicks ?? 0, locale)} />
        <Kpi label={t("advertising:kpi.ctr")} value={`${totals?.ctr ?? 0}%`} />
        <Kpi
          label={t("advertising:kpi.cpc")}
          value={money(String(totals?.cpc ?? 0), "USD", locale)}
        />
      </div>
      <p className="text-xs text-muted-foreground">{t("advertising:combinedNote")}</p>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2" role="tablist">
        {(["overview", "campaigns", "skus", "orders"] as Tab[]).map((key) => (
          <Button
            key={key}
            role="tab"
            aria-selected={tab === key}
            variant={tab === key ? "default" : "outline"}
            size="sm"
            onClick={() => setTab(key)}
          >
            {t(`advertising:tabs.${key}`)}
          </Button>
        ))}
      </div>

      {tab === "overview" ? (
        <>
          {/* Advisory only -- the autopilot's own dry-run output, per its
              header: nothing here writes to Amazon or implies approval. Card
              is simply absent until a run has actually produced one, rather
              than showing an empty-state for a feature Isaac hasn't seen
              fire yet. */}
          {recommendationsQuery.data?.data.length ? (
            <Card>
              <CardHeader className="gap-1">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Lightbulb className="h-4 w-4" aria-hidden />
                  {t("advertising:recommendations.title")}
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  {t("advertising:recommendations.subtitle")}
                  {recommendationsQuery.data.meta.runAt
                    ? ` · ${t("advertising:recommendations.lastRun")} ${whenever(recommendationsQuery.data.meta.runAt, locale)}`
                    : ""}
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                {recommendationsQuery.data.data.map((r) => (
                  <div key={r.id} className="rounded-lg border p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-2 font-medium">
                        <span className="truncate">{r.campaignName}</span>
                        <Badge variant="outline" className="shrink-0 capitalize">
                          {r.marketplace}
                        </Badge>
                      </div>
                      <Badge variant="secondary" className="shrink-0">
                        {t(`advertising:recommendations.action.${r.actionType}`)}
                      </Badge>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">{r.reason}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t("advertising:trend.title")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[20rem] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trendQuery.data ?? []}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    {/* An explicit YAxis is REQUIRED, not decorative: omitting it
                      once truncated a 31-day series to about a third of its
                      real height (see the Sales trend chart's own history). */}
                    <YAxis tick={{ fontSize: 12 }} domain={[0, "auto"]} width={70} />
                    <RechartsTooltip />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="spend"
                      name={t("advertising:trend.spend")}
                      stroke="hsl(var(--primary))"
                      fill="hsl(var(--primary) / 0.2)"
                    />
                    <Area
                      type="monotone"
                      dataKey="sales"
                      name={t("advertising:trend.sales")}
                      stroke="hsl(var(--success))"
                      fill="hsl(var(--success) / 0.2)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Per-channel breakdown: the "view them separately" half of the ask,
                without leaving the consolidated page. */}
              <div className="mt-6 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("advertising:campaigns.channel")}</TableHead>
                      <TableHead className="text-right">{t("advertising:kpi.spend")}</TableHead>
                      <TableHead className="text-right">{t("advertising:kpi.sales")}</TableHead>
                      <TableHead className="text-right">{t("advertising:kpi.acos")}</TableHead>
                      <TableHead className="text-right">{t("advertising:kpi.clicks")}</TableHead>
                      <TableHead className="text-right">{t("advertising:kpi.ctr")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(summaryQuery.data?.byChannel ?? []).map((c) => (
                      <TableRow key={c.channelId}>
                        <TableCell className="font-medium capitalize">{c.marketplace}</TableCell>
                        <TableCell className="text-right">
                          {money(c.spend, "USD", locale)}
                        </TableCell>
                        <TableCell className="text-right">
                          {money(c.attributedSales, "USD", locale)}
                        </TableCell>
                        <TableCell className="text-right">
                          {c.acos != null ? `${c.acos}%` : "—"}
                        </TableCell>
                        <TableCell className="text-right">{integer(c.clicks, locale)}</TableCell>
                        <TableCell className="text-right">{c.ctr}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      ) : null}

      {tab === "campaigns" ? (
        <Card>
          <CardHeader className="gap-2">
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-base">{t("advertising:campaigns.title")}</CardTitle>
              <Button size="sm" onClick={() => setNewCampaignOpen(true)}>
                <Plus className="mr-1 h-4 w-4" />
                {t("advertising:newCampaign.button")}
              </Button>
            </div>
            <NewCampaignDialog open={newCampaignOpen} onOpenChange={setNewCampaignOpen} />
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Select
                value={campaignFilter}
                onValueChange={(v) => setCampaignFilter(v as typeof campaignFilter)}
              >
                <SelectTrigger
                  className="w-[12rem]"
                  aria-label={t("advertising:campaigns.filterBy")}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("advertising:campaigns.filterAll")}</SelectItem>
                  <SelectItem value="enabled">
                    {t("advertising:campaigns.filterEnabled")}
                  </SelectItem>
                </SelectContent>
              </Select>
              {campaignFilter === "enabled" && campaignsQuery.data ? (
                <p className="text-xs text-muted-foreground">
                  {t("advertising:campaigns.filterHint", {
                    shown: sortedCampaigns.length,
                    total: campaignsQuery.data.length,
                  })}
                </p>
              ) : null}
            </div>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {campaignActionError ? (
              <div className="mb-3 flex items-center justify-between gap-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                <span>{campaignActionError}</span>
                <Button size="sm" variant="ghost" onClick={() => setCampaignActionError(null)}>
                  {t("advertising:campaigns.dismiss")}
                </Button>
              </div>
            ) : null}
            <Table className="table-fixed">
              <TableHeader>
                <TableRow>
                  <SortableHead
                    className="w-[18rem]"
                    label={t("advertising:campaigns.name")}
                    sortKey="name"
                    active={campaignSort}
                    onSort={toggleCampaignSort}
                  />
                  <TableHead className="w-[6rem]">{t("advertising:campaigns.channel")}</TableHead>
                  <SortableHead
                    className="w-[7rem]"
                    label={t("advertising:campaigns.status")}
                    sortKey="status"
                    active={campaignSort}
                    onSort={toggleCampaignSort}
                  />
                  <TableHead className="w-[8rem]">{t("advertising:campaigns.type")}</TableHead>
                  <SortableHead
                    align="right"
                    className="w-[9rem]"
                    label={t("advertising:campaigns.budget")}
                    sortKey="budget"
                    active={campaignSort}
                    onSort={toggleCampaignSort}
                  />
                  <SortableHead
                    align="right"
                    className="w-[7rem]"
                    label={t("advertising:kpi.impressions")}
                    sortKey="impressions"
                    active={campaignSort}
                    onSort={toggleCampaignSort}
                  />
                  <SortableHead
                    align="right"
                    className="w-[7rem]"
                    label={t("advertising:kpi.clicks")}
                    sortKey="clicks"
                    active={campaignSort}
                    onSort={toggleCampaignSort}
                  />
                  <SortableHead
                    align="right"
                    className="w-[7rem]"
                    label={t("advertising:kpi.cpc")}
                    sortKey="cpc"
                    active={campaignSort}
                    onSort={toggleCampaignSort}
                  />
                  <SortableHead
                    align="right"
                    className="w-[8rem]"
                    label={t("advertising:kpi.spend")}
                    sortKey="spend"
                    active={campaignSort}
                    onSort={toggleCampaignSort}
                  />
                  <SortableHead
                    align="right"
                    className="w-[8rem]"
                    label={t("advertising:kpi.sales")}
                    sortKey="sales"
                    active={campaignSort}
                    onSort={toggleCampaignSort}
                  />
                  <SortableHead
                    align="right"
                    className="w-[6rem]"
                    label={t("advertising:kpi.acos")}
                    sortKey="acos"
                    active={campaignSort}
                    onSort={toggleCampaignSort}
                  />
                  <SortableHead
                    align="right"
                    className="w-[6rem]"
                    label={t("advertising:kpi.roas")}
                    sortKey="roas"
                    active={campaignSort}
                    onSort={toggleCampaignSort}
                  />
                  <TableHead className="w-[10rem]">{t("advertising:campaigns.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedCampaigns.map((c) => {
                  const controllable = canControlCampaign(c.marketplace);
                  const canToggle =
                    controllable && (c.status === "enabled" || c.status === "paused");
                  return (
                    <TableRow key={c.id}>
                      <TableCell className="truncate font-medium" title={c.name}>
                        {c.name}
                      </TableCell>
                      <TableCell className="capitalize">{c.marketplace}</TableCell>
                      <TableCell>
                        {/* SYSTEM_PAUSED is called out rather than shown as an
                            ordinary pause: eBay paused it, not a person, and the
                            fix is an account problem outside this app. */}
                        {c.platformStatus === "SYSTEM_PAUSED" ? (
                          <Badge
                            variant="destructive"
                            title={t("advertising:status.systemPausedHint")}
                          >
                            {t("advertising:status.systemPaused")}
                          </Badge>
                        ) : (
                          <Badge variant="outline">{t(`advertising:status.${c.status}`)}</Badge>
                        )}
                      </TableCell>
                      <TableCell className="truncate text-muted-foreground">
                        {c.campaignType ?? "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <span>
                            {c.dailyBudget != null ? money(c.dailyBudget, c.currency, locale) : "—"}
                          </span>
                          {controllable ? (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 min-h-0 min-w-0"
                              aria-label={t("advertising:campaigns.editBudget")}
                              onClick={() => {
                                setBudgetDialog({
                                  campaignId: c.id,
                                  name: c.name,
                                  currency: c.currency,
                                });
                                setBudgetDraft(c.dailyBudget ?? "");
                              }}
                            >
                              <Pencil className="h-3.5 w-3.5" aria-hidden />
                            </Button>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">{integer(c.impressions, locale)}</TableCell>
                      <TableCell className="text-right">{integer(c.clicks, locale)}</TableCell>
                      <TableCell className="text-right">
                        {money(String(c.cpc), "USD", locale)}
                      </TableCell>
                      <TableCell className="text-right">{money(c.spend, "USD", locale)}</TableCell>
                      <TableCell className="text-right">
                        {money(c.attributedSales, "USD", locale)}
                      </TableCell>
                      <TableCell className="text-right">
                        {c.acos != null ? `${c.acos}%` : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        {c.roas != null ? `${c.roas}×` : "—"}
                      </TableCell>
                      <TableCell>
                        {controllable ? (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={campaignStateMutation.isPending || !canToggle}
                            onClick={() =>
                              campaignStateMutation.mutate({
                                id: c.id,
                                state: c.status === "paused" ? "enabled" : "paused",
                              })
                            }
                          >
                            {c.status === "paused" ? (
                              <>
                                <Play className="h-3.5 w-3.5" aria-hidden />
                                {t("advertising:campaigns.enable")}
                              </>
                            ) : (
                              <>
                                <Pause className="h-3.5 w-3.5" aria-hidden />
                                {t("advertising:campaigns.pause")}
                              </>
                            )}
                          </Button>
                        ) : (
                          <span
                            className="text-xs text-muted-foreground"
                            title={t("advertising:campaigns.writeNotSupported")}
                          >
                            —
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {sortedCampaigns.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} className="py-8 text-center text-muted-foreground">
                      {campaignFilter === "enabled" && (campaignsQuery.data?.length ?? 0) > 0
                        ? t("advertising:campaigns.emptyEnabled")
                        : t("advertising:campaigns.empty")}
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : null}

      {tab === "skus" ? (
        <Card>
          <CardHeader className="gap-2">
            <CardTitle className="text-base">{t("advertising:skus.title")}</CardTitle>
            <p className="text-sm text-muted-foreground">{t("advertising:skus.explain")}</p>
            <div className="flex flex-wrap gap-2 pt-1">
              <Select value={matched} onValueChange={(v) => setMatched(v as typeof matched)}>
                <SelectTrigger className="w-[12rem]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("advertising:skus.filterAll")}</SelectItem>
                  <SelectItem value="matched">{t("advertising:skus.filterMatched")}</SelectItem>
                  <SelectItem value="unmatched">{t("advertising:skus.filterUnmatched")}</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sort} onValueChange={(v) => setSort(v as typeof sort)}>
                <SelectTrigger className="w-[11rem]" aria-label={t("advertising:skus.sortBy")}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="spend">{t("advertising:kpi.spend")}</SelectItem>
                  <SelectItem value="sales">{t("advertising:kpi.sales")}</SelectItem>
                  <SelectItem value="acos">{t("advertising:kpi.acos")}</SelectItem>
                  <SelectItem value="clicks">{t("advertising:kpi.clicks")}</SelectItem>
                  <SelectItem value="impressions">{t("advertising:kpi.impressions")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table className="table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[14rem]">{t("advertising:skus.sku")}</TableHead>
                  <TableHead className="w-[20rem]">{t("advertising:skus.product")}</TableHead>
                  <TableHead className="w-[8rem] text-right">
                    {t("advertising:kpi.spend")}
                  </TableHead>
                  <TableHead className="w-[9rem] text-right">
                    {t("advertising:kpi.sales")}
                  </TableHead>
                  <TableHead className="w-[9rem] text-right">
                    {t("advertising:skus.realRevenue")}
                  </TableHead>
                  <TableHead
                    className="w-[7rem] text-right"
                    title={t("advertising:skus.trueAcosHint")}
                  >
                    {t("advertising:skus.trueAcos")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(skusQuery.data ?? []).map((r) => (
                  <TableRow key={`${r.marketplace}-${r.channelSku}`}>
                    <TableCell className="truncate font-mono text-xs" title={r.channelSku}>
                      {r.channelSku}
                    </TableCell>
                    <TableCell className="truncate" title={r.productTitle ?? ""}>
                      {r.productId ? (
                        (r.productTitle ?? r.productSku)
                      ) : (
                        <Badge variant="outline" title={t("advertising:skus.unmatchedHint")}>
                          {t("advertising:skus.unmatchedBadge")}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">{money(r.spend, "USD", locale)}</TableCell>
                    <TableCell className="text-right">
                      {money(r.attributedSales, "USD", locale)}
                    </TableCell>
                    <TableCell className="text-right">
                      {r.realRevenue != null ? money(r.realRevenue, "USD", locale) : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      {r.trueAcos != null ? `${r.trueAcos}%` : "—"}
                    </TableCell>
                  </TableRow>
                ))}
                {skusQuery.data?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                      {t("advertising:skus.empty")}
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : null}

      {tab === "orders" ? (
        <Card>
          <CardHeader className="gap-1">
            <CardTitle className="text-base">{t("advertising:orders.title")}</CardTitle>
            <p className="text-sm text-muted-foreground">{t("advertising:orders.explain")}</p>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table className="table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[10rem]">{t("advertising:orders.orderNumber")}</TableHead>
                  <TableHead className="w-[6rem]">{t("advertising:campaigns.channel")}</TableHead>
                  <TableHead className="w-[7rem]">{t("advertising:campaigns.status")}</TableHead>
                  <TableHead className="w-[12rem]">{t("advertising:orders.buyer")}</TableHead>
                  <TableHead className="w-[6rem] text-right">
                    {t("advertising:orders.items")}
                  </TableHead>
                  <TableHead className="w-[8rem] text-right">
                    {t("advertising:orders.total")}
                  </TableHead>
                  <TableHead className="w-[10rem]">{t("advertising:orders.orderedAt")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(activeOrdersQuery.data ?? []).map((o) => (
                  <TableRow key={o.id}>
                    <TableCell className="truncate font-mono text-xs" title={o.orderNumber}>
                      {o.orderNumber}
                    </TableCell>
                    <TableCell className="capitalize">{o.marketplace}</TableCell>
                    <TableCell>
                      <Badge variant={ORDER_STATUS_VARIANT[o.status]}>
                        {t(`sales:status.${o.status}`)}
                      </Badge>
                    </TableCell>
                    <TableCell className="truncate">{o.buyerName ?? "—"}</TableCell>
                    <TableCell className="text-right">{integer(o.itemCount, locale)}</TableCell>
                    <TableCell className="text-right">
                      {money(o.total, o.currency, locale)}
                    </TableCell>
                    <TableCell>{whenever(o.orderedAt, locale)}</TableCell>
                  </TableRow>
                ))}
                {activeOrdersQuery.data?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                      {t("advertising:orders.empty")}
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : null}

      {/* Manual budget edit — a Dialog rather than an inline table-cell input,
          matching the edit-in-a-dialog pattern the rest of the app uses (see
          Inventory's edit-SKU dialog) instead of cramming a 44px tap target
          (stack §10b) into a table row. */}
      <Dialog open={budgetDialog !== null} onOpenChange={(open) => !open && setBudgetDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t("advertising:campaigns.editBudget")}</DialogTitle>
            <DialogDescription>{budgetDialog?.name}</DialogDescription>
          </DialogHeader>
          {budgetDialog ? (
            <div className="space-y-3">
              <div>
                <Label htmlFor="campaign-budget-field">{t("advertising:campaigns.budget")}</Label>
                <Input
                  id="campaign-budget-field"
                  type="number"
                  step="0.01"
                  min="0"
                  value={budgetDraft}
                  onChange={(e) => setBudgetDraft(e.target.value)}
                  className="mt-1 tabular-nums"
                />
                <p className="mt-1 text-xs text-muted-foreground">{budgetDialog.currency}</p>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setBudgetDialog(null)}>
                  {t("advertising:campaigns.cancel")}
                </Button>
                <Button
                  type="button"
                  disabled={campaignBudgetMutation.isPending || budgetDraft.trim() === ""}
                  onClick={() =>
                    campaignBudgetMutation.mutate({
                      id: budgetDialog.campaignId,
                      dailyBudget: budgetDraft,
                    })
                  }
                >
                  {campaignBudgetMutation.isPending
                    ? t("advertising:campaigns.saving")
                    : t("advertising:campaigns.save")}
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
      </CardContent>
    </Card>
  );
}

/** A clickable TableHead that toggles/reports campaign-table sort state. */
function SortableHead({
  label,
  sortKey,
  active,
  onSort,
  align,
  className,
}: {
  label: string;
  sortKey: CampaignSortKey;
  active: { key: CampaignSortKey; dir: SortDir };
  onSort: (key: CampaignSortKey) => void;
  align?: "right";
  className?: string;
}) {
  const isActive = active.key === sortKey;
  return (
    <TableHead
      className={cn(align === "right" && "text-right", className)}
      aria-sort={isActive ? (active.dir === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        className={cn(
          "inline-flex items-center gap-1 hover:text-foreground",
          align === "right" && "flex-row-reverse",
        )}
        onClick={() => onSort(sortKey)}
      >
        {label}
        <ArrowUpDown
          className={cn("h-3 w-3", isActive ? "opacity-100" : "opacity-30")}
          aria-hidden
        />
      </button>
    </TableHead>
  );
}
